import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Printer } from 'lucide-react'; 
import AddCustomerForm from '../components/AddCustomerForm'; 
import PolicyActionsModal from '../components/PolicyActionsModal'; // ✅ NEW IMPORT
import SafewingsLogo from '../assets/safewings_logo.png'; 

// --- Helper function to retrieve customer data from localStorage ---
const getCustomerData = (customerId) => {
    const customers = JSON.parse(localStorage.getItem("customers_list") || "[]");
    return customers.find(c => c.id === customerId);
};

const CustomerViewPage = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();

    // Load initial data
    const [viewCustomerData, setViewCustomerData] = useState(() => getCustomerData(customerId));
    const [isEditing, setIsEditing] = useState(false);
    
    // ✅ NEW STATE FOR POLICY ACTIONS
    const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);
    const [message, setMessage] = useState(""); // For temporary status messages

    // Update the viewed customer data whenever localStorage changes (e.g., after an edit/action)
    useEffect(() => {
        // This is a simple way to reload data if the list is updated elsewhere (e.g., the List Page)
        const handleStorageChange = () => {
            setViewCustomerData(getCustomerData(customerId));
        };

        // Re-check customer data on mount in case the customer was archived/deleted elsewhere
        handleStorageChange(); 

        // Add a listener (optional, but good practice for multi-page data sync)
        window.addEventListener('storage', handleStorageChange);

        return () => window.removeEventListener('storage', handleStorageChange);
    }, [customerId]); 

    // --- Utility function for showing temporary messages ---
    const showMessage = (msg) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
    };

    const handlePrint = () => {
        window.print();
    };

    if (!viewCustomerData) {
        // If customer was archived/deleted, show the "Not Found" message
        return (
            <div className="max-w-6xl mx-auto p-6 mt-10">
                <button
                    onClick={() => navigate('/customers')} 
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 font-medium mb-4"
                >
                    <ArrowLeft size={16} />
                    <span>Back to List</span>
                </button>
                <h1 className="text-3xl font-bold text-red-600">Customer Not Found or Archived</h1>
                <p className='text-gray-500 mt-2'>The customer record with ID: **{customerId}** may have been permanently deleted or moved to an archive.</p>
            </div>
        );
    }

    // --- Data Grouping based on the Form Fields (Used for Display) ---
    const dataGroups = {
        "Personal Information": [
            "Driver Name*", "ID Card Number Production Point*", "Driver's License Number*",
            "License Category", "Date of Birth", "Telephone Number",
            "Occupation", "Trade Register",
        ],
        "Vehicle Information": [
            "Make*", "Registration Number", "Chassis Number",
            "Power and Energy", "Number of Seats", "Payload",
            "Attestation Number", "Pink Card Number", "Date of First Registration",
            "Date of Issue",
        ],
        "Insurance Details": [
            "Policy Number", "Name of the Insured", "Category", 
            "Driving Zone", "Effective Date", "Expiration Date", 
            "Number of days", "Date of Resignation (Optional)", "Coverage Subscribed",
            "Advisors", "Status",
        ],
        "Financial Information": [
            "Net Premium (XAF)", "Accidental Damages (XAF)", "Civil Liability (XAF)",
            "VAT (XAF)", "Insurance (XAF)", "Taxable Income Base (XAF)",
            "Taxable Income Additional (XAF)", "Actual Collection (XAF)",
        ],
    };

    // --- HANDLERS ---
    
    // Consolidated update function for both form edit and policy actions
    const updateCustomerStateAndStorage = (updatedCustomer) => {
        const customers = JSON.parse(localStorage.getItem("customers_list") || "[]");
        
        let newCustomersList = customers;

        // Check if the update resulted in an 'Archive' action
        if (updatedCustomer.Action === "Archive") {
            newCustomersList = customers.filter(c => c.id !== customerId);
            // After archiving, navigate back to the list
            navigate('/customers'); 
        } else {
             // Standard update logic for Edit, Renew, Terminate
             newCustomersList = customers.map(c => 
                c.id === customerId ? updatedCustomer : c
            );
        }

        localStorage.setItem("customers_list", JSON.stringify(newCustomersList));
        
        // Refresh the local state for the current view
        // If archived, this will be null and trigger the "Not Found" message on re-render.
        setViewCustomerData(newCustomersList.find(c => c.id === customerId));
    };

    // 1. EDIT SUBMIT HANDLER
    const handleEditSubmit = (formData) => {
        // Logic similar to CustomerList's handleFormSubmit (but only for the one customer)
        const updatedCustomerObject = {
            ...viewCustomerData,
            ...formData, 
            name: formData["Driver Name*"] || viewCustomerData.name,
            policy: formData["Policy Number"] || viewCustomerData.policy,
            phone: formData["Telephone Number"] || viewCustomerData.phone,
            vehicle: formData["Make*"] || viewCustomerData.vehicle,
            reg: formData["Registration Number"] || viewCustomerData.reg,
            status: formData["Status"] || viewCustomerData.status,
            premium: formData["Net Premium (XAF)"] || viewCustomerData.premium,
            expiry: formData["Expiration Date"] || viewCustomerData.expiry, // Note: This field is YYYY-MM-DD from form, but stored as DD/MM/YYYY in list. For this deep view, we stick to the form's format if present.
            expired: formData["Expiration Date"] ? new Date(formData["Expiration Date"]) < new Date() : viewCustomerData.expired,
        };

        updateCustomerStateAndStorage(updatedCustomerObject);
        setIsEditing(false);
        showMessage("Customer details updated successfully!");
    };

    // 2. POLICY ACTION HANDLER (Renew, Terminate, Archive)
    const handlePolicyAction = (id, data, successMessage) => {
        const { Action, ...updateFields } = data;

        if (Action === "Archive") {
            // Archive action doesn't require further field updates, just removal
            updateCustomerStateAndStorage({ Action: "Archive" });
        } else {
            // RENEW / TERMINATE: Apply changes and recalculate status/expiry
            const updatedCustomer = { 
                ...viewCustomerData, 
                ...updateFields 
            };
            
            // Recalculate 'expired' status based on the new expiry date
            if (updatedCustomer["Expiration Date"] && updatedCustomer["Expiration Date"] !== 'N/A') {
                updatedCustomer.expired = new Date(updatedCustomer["Expiration Date"]) < new Date();
            } else if (updatedCustomer.Status === 'Inactive' || updatedCustomer.Status === 'Terminated') {
                updatedCustomer.expired = true;
            }

            // Also update the summary fields used in the list view
            updatedCustomer.status = updatedCustomer.Status;
            updatedCustomer.policy = updatedCustomer["Policy Number"];
            updatedCustomer.premium = updatedCustomer["Net Premium (XAF)"];
            updatedCustomer.expiry = updatedCustomer["Expiration Date"]; // Keep YYYY-MM-DD format for internal use here
            
            updateCustomerStateAndStorage(updatedCustomer);
        }

        showMessage(successMessage);
        setIsActionsModalOpen(false);
    };

    const handleCloseEdit = () => {
        setIsEditing(false);
    };


    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Status Message */}
            {message && (
                <div className="fixed top-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-xl z-50 transition duration-300">
                    {message}
                </div>
            )}
            
            {/* PRINT-ONLY HEADER */}
            <div className="hidden print:flex justify-between items-center pb-4 mb-8 border-b border-gray-300">
                <div className="flex items-center space-x-3">
                    <img 
                        src={SafewingsLogo} 
                        alt="Safewings Insurance Logo" 
                        className="h-10 w-auto" 
                    />
                </div>
                {/* Print Date */}
                <p className="text-sm text-gray-600">
                    Report Date: {new Date().toLocaleDateString()}
                </p>
            </div>


            {/* Existing: Back Button / Actions Buttons (Hidden on print) */}
            <div className="flex justify-between items-center mb-6 print:hidden">
                <button
                    onClick={() => navigate(-1)} 
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 font-medium"
                >
                    <ArrowLeft size={16} />
                    <span>Back to List</span>
                </button>

                <div className="flex space-x-4">
                    {/* ✅ NEW: Policy Actions Button */}

                    <button 
                        onClick={handlePrint}
                        className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 py-2 rounded shadow-sm hover:bg-gray-50 transition duration-150"
                    >
                        <Printer size={16} />
                        <span>Print Details</span>
                    </button>

                    <button 
                        onClick={() => setIsEditing(true)}
                        className="flex items-center space-x-2 bg-gray-900 text-white px-4 py-2 rounded shadow hover:bg-gray-700 transition duration-150"
                    >
                        <Edit size={16} />
                        <span>Edit Customer</span>
                    </button>
                </div>
            </div>

            {/* Main Content Card */}
            <div className="bg-white rounded-xl shadow-2xl p-8 print:shadow-none print:p-0">
                
                {/* Main Header (Screen & Print) */}
                <div className="mb-8 pb-4 border-b border-gray-200">
                    <h1 className="text-3xl font-extrabold text-gray-900 leading-tight">
                        {viewCustomerData["Driver Name*"] || viewCustomerData.name}
                    </h1>
                    <p className="text-lg font-medium text-gray-500 mt-1">
                        Customer ID: **{viewCustomerData.id}** &middot; Policy: **{viewCustomerData["Policy Number"] || 'N/A'}**
                    </p>
                    {viewCustomerData.expired && (
                        <span className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-600 text-white">
                            POLICY EXPIRED
                        </span>
                    )}
                </div>


                {/* --- Render Data Groups --- */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 print:grid-cols-2"> 
                    {Object.entries(dataGroups).map(([groupTitle, fields]) => (
                        <div key={groupTitle} className="border border-gray-200 rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                                {/* Using icon placeholders here */}
                                {groupTitle === "Personal Information" && <span className="text-xl">👤</span>}
                                {groupTitle === "Vehicle Information" && <span className="text-xl">🚗</span>}
                                {groupTitle === "Insurance Details" && <span className="text-xl">📄</span>}
                                {groupTitle === "Financial Information" && <span className="text-xl">💰</span>}
                                {groupTitle}
                            </h2>
                            <div className="space-y-3 text-sm">
                                {fields.map(field => {
                                    // Clean key to match stored data
                                    const key = field.replace(/\*|\(XAF\)/g, '').trim();
                                    const value = viewCustomerData[key] || viewCustomerData[field] || 'N/A'; // Check both cleaned key and full key
                                    
                                    const isExpiredField = key === "Expiration Date";
                                    const isExpired = viewCustomerData.expired;

                                    return (
                                        <div key={key}>
                                            <p className="font-medium text-gray-600 leading-tight">{field.replace('*', '').replace('(XAF)', '').trim()}</p>
                                            {isExpiredField && isExpired ? (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    Expired: {value}
                                                </span>
                                            ) : (
                                                <p className="text-gray-900 font-semibold">{value}</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* System Information */}
                <div className="pt-8 mt-8 border-t border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">System Information</h2>
                    <div className="grid grid-cols-2 gap-8 text-sm">
                        <div>
                            <p className="font-medium text-gray-600">Customer ID</p>
                            <p className="text-gray-900 font-semibold">{viewCustomerData.id}</p> 
                        </div>
                        <div>
                            <p className="font-medium text-gray-600">Last Policy Action</p>
                            <p className="text-gray-900 font-semibold">{viewCustomerData["Date of Issue"] || 'N/A'}</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* --- MODAL RENDERING: EDIT --- */}
            {isEditing && (
                <AddCustomerForm 
                    initialData={viewCustomerData} 
                    onSubmit={handleEditSubmit} 
                    onClose={handleCloseEdit} 
                />
            )}

            {/* --- MODAL RENDERING: POLICY ACTIONS --- */}
            {isActionsModalOpen && (
                <PolicyActionsModal
                    customer={viewCustomerData}
                    onAction={handlePolicyAction}
                    onClose={() => setIsActionsModalOpen(false)}
                />
            )}
        </div>
    );
};

export default CustomerViewPage;